import { useInfiniteQuery, type InfiniteData, type QueryKey, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

export const DEFAULT_PAGE_SIZE = 50;

type RecordPage<TItem> = { items: TItem[]; hasMore: boolean };

type UseInfiniteRecordListResult<TItem> = {
  items: TItem[];
  query: UseInfiniteQueryResult<InfiniteData<RecordPage<TItem>, number>, Error>;
  bottomSentinelRef: (node?: Element | null) => void;
};

type UseInfiniteRecordListArgs<TItem> = {
  /**
   * The query key for this list. Build it from the tRPC `.list.queryKey({...})` helper so it stays
   * prefix-compatible with `queryFilter` invalidation. The tRPC `infiniteQueryKey` helper can't be
   * used here — these procedures paginate by `offset`, not a `cursor` field.
   */
  queryKey: QueryKey;
  /** Fetch one page. Adapt the tRPC response into `{ items, hasMore }` here. */
  queryFn: (params: { offset: number; count: number }) => Promise<RecordPage<TItem>>;
  pageSize?: number;
  enabled?: boolean;
};

/**
 * Drives an offset-paginated record list with infinite scroll. Flattens the fetched pages into a
 * single array for the list component and returns a `bottomSentinelRef` to place after the list —
 * scrolling it into view fetches the next page.
 */
export const useInfiniteRecordList = <TItem>({
  queryKey,
  queryFn,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled,
}: UseInfiniteRecordListArgs<TItem>): UseInfiniteRecordListResult<TItem> => {
  // Pin the generics — notably the `number` page-param type — so the inferred result type matches
  // the annotated return type instead of widening the page param to `unknown`.
  const query = useInfiniteQuery<RecordPage<TItem>, Error, InfiniteData<RecordPage<TItem>, number>, QueryKey, number>({
    queryKey,
    queryFn: async ({ pageParam }) => queryFn({ offset: pageParam, count: pageSize }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastOffset) => (lastPage.hasMore ? lastOffset + pageSize : undefined),
    enabled,
  });

  const pages = query.data?.pages;
  const items = useMemo(() => pages?.flatMap((page) => page.items) ?? [], [pages]);

  const { ref: bottomSentinelRef, inView } = useInView({ rootMargin: '200px' });

  // Drive paging off the sentinel's `inView` state (not `onChange`): `onChange` only fires on
  // intersection *transitions*, so a page that renders few rows — because the view filters or groups
  // the results (e.g. Labs keeps only `result` observations) — leaves the sentinel continuously in
  // view and would never re-trigger. Re-running on `pages` after each page settles keeps fetching
  // until the viewport fills or the server is exhausted.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, pages]);

  return { items, query, bottomSentinelRef };
};
